import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { dataProps } from '@ds/react-utils'
import { MotionPresence } from '@ds/motion'
import {
  Popover,
  PopoverStrategy,
  PopoverAlignment,
  PopoverLocation,
  HideMiddleware,
} from '../Popover/index'
import { zIndexes } from '../../variables'
import { usePopoverPositionUpdate } from '../../hooks/usePopoverPositionUpdate'
import { consoleWarn } from '../../logging'
import { useDsUiDataProps } from '../../theming'
import { CustomPropTypes } from '../../support'
import type { DivForwardRef } from '../../types'
import { TooltipUI } from './TooltipUI'

export const POINTER_SIZE = 7

/**
 * Used for centerArrow feature which helps arrow placement when the anchor/trigger
 * is smaller than the size of the Tooltip.
 */
const alignmentToDimension = {
  above: 'width',
  below: 'width',
  before: 'height',
  after: 'height',
} as const

export type TooltipLocation = PopoverLocation
export type TooltipAlignment = PopoverAlignment

export interface TooltipProps {
  /**
   * The alignment of the Tooltip along the edge of its anchor element.
   *
   * For locations 'above' and 'below':
   * - 'start': start-aligns the Tooltip and the anchor element
   * - 'center': centers the Tooltip along the width of the anchor element
   * - 'end': end-aligns the Tooltip and the anchor element
   *
   * For locations 'before' and 'after':
   * - 'start': top-aligns the Tooltip and the anchor element
   * - 'center': centers the Tooltip along the height of the anchor element
   * - 'end': bottom-aligns the Tooltip and the anchor element
   */
  alignment?: TooltipAlignment
  /**
   * The Element around which the Tooltip will be positioned.
   */
  anchorElement?: HTMLElement | null
  /**
   * Centers the Tooltip's arrow along the length of the anchor element.
   *
   * For small anchor elements combined with `start` or `end` alignments the
   * default arrow positioning may not point to the anchor. When set to `true`
   * the Tooltip's pointer will do a calculation to attempt to point to the
   * center of the anchor element while remaining in the correct alignment.
   *
   * Due to the width of the arrow some anchor sizes will always have issues
   * pointing to the anchor, in those scenarios use `center` alignment.
   */
  centerArrow?: boolean
  /**
   * Fine tunes the Tooltip offset away from the anchor element.
   */
  distance?: number
  /**
   * A React ref to assign to the HTML node representing the Tooltip component.
   */
  forwardedRef?: DivForwardRef
  /**
   * For situations where you want to hide the Tooltip because it appears detached from the reference element
   * or attached to nothing. Accepts an array of the following values.
   *
   * `["referenceHidden"]` Hides the Tooltip when the reference element is clipped out of view.
   *
   * `["escaped"]` Hides the Tooltip when the Tooltip has “escaped” the reference’s clipping context and appears fully detached from it.
   *
   * `["referenceHidden", "escaped"]` When you need both of the above.
   */
  hideStrategy?: HideMiddleware[]
  /**
   * An unique identifier (ID) in the whole document.
   * Its purpose is to identify the element when linking, scripting, or styling (with CSS).
   */
  id?: string
  /**
   * The preferred location of the Tooltip relative to its anchor element.
   */
  location?: TooltipLocation
  /**
   * By default the tooltip will 'flip' if its content reaches scroll parent boundaries.
   * With locationFixed enabled the tooltip will stay in its specified location & alignment.
   */
  locationFixed?: boolean
  /**
   * Fine tunes the Tooltip offset along the anchor element.
   */
  skidding?: number
  /**
   * The default strategy for Tooltip is to use absolute positioning.  In some DOM structures
   * it may be necessary to use 'fixed' to bust out of layout/positioning rules established
   * by the Tooltips's ancestors.
   */
  strategy?: PopoverStrategy
  /**
   * The text to display inside the Tooltip.
   */
  text: string
  /**
   * Displays the Tooltip.
   */
  visible?: boolean
  /**
   * Accepts custom data attribute
   */
  'data-.*'?: string
  'data-qa'?: string
}

/**
 * Tooltips are user-triggered pieces of contextual help that briefly explain the function of an element.
 */
export function Tooltip({
  alignment = 'center',
  anchorElement,
  centerArrow = false,
  distance,
  forwardedRef,
  hideStrategy,
  id,
  location = 'above',
  locationFixed = false,
  skidding = 0,
  strategy = 'absolute',
  text,
  visible = false,
  ...restProps
}: TooltipProps) {
  const anchorRef = useRef(anchorElement)
  const [anchorLength, setAnchorLength] = useState<number | null>(null)

  const dsUiDataProps = useDsUiDataProps(Tooltip)

  /**
   * When anchorElement is null we return the tooltip content (no Popover) and
   * the consumer is responsible for positioning it themselves.
   *
   * Consumers that re-use a single Tooltip instance may set the anchor to null
   * when closing the tooltip (visible = false). This is unnecessary and results
   * in the Tooltip jumping back to content-order (position: relative) before
   * the exit animation completes.
   *
   * To prevent this flash, we don't allow the anchor to be set to null after
   * it has been set to a non-null value (an element).
   */
  if (anchorRef.current && !anchorElement) {
    consoleWarn(
      `After passing Tooltip a valid anchorElement setting the anchorElement to a falsy value is unnecessary. Just pass the next element to reposition the Tooltip.`,
    )
  } else {
    anchorRef.current = anchorElement
  }

  const offsetMiddleware = Popover.presets.offset(
    skidding,
    distance ? distance + POINTER_SIZE : POINTER_SIZE,
  )

  const popoverUpdate = usePopoverPositionUpdate(alignment, location)

  React.useEffect(() => {
    if (centerArrow && alignment !== 'center') {
      const dimension = alignmentToDimension[location]
      const length = anchorElement?.getBoundingClientRect?.()[dimension]

      if (length) {
        setAnchorLength(length)
      }
    }
  }, [alignment, anchorElement, centerArrow, location])

  return anchorRef.current ? (
    <MotionPresence>
      {visible && (
        <Popover
          alignment={alignment}
          anchorElement={anchorRef.current}
          containerStyles={{ zIndex: zIndexes.Tooltip }}
          hideMiddleware={hideStrategy}
          isSpanContainer
          location={location}
          locationFixed={locationFixed}
          offsetMiddleware={offsetMiddleware}
          onUpdate={popoverUpdate.trackCurrentPosition}
          strategy={strategy}
        >
          <TooltipUI
            {...dsUiDataProps}
            {...dataProps(restProps)}
            alignment={popoverUpdate.alignment}
            anchorLength={anchorLength || undefined}
            forwardedRef={forwardedRef}
            id={id}
            location={popoverUpdate.location}
            text={text}
          />
        </Popover>
      )}
    </MotionPresence>
  ) : (
    <MotionPresence>
      {visible && (
        <TooltipUI
          {...dsUiDataProps}
          {...dataProps(restProps)}
          alignment={popoverUpdate.alignment}
          anchorLength={anchorLength || undefined}
          forwardedRef={forwardedRef}
          id={id}
          location={popoverUpdate.location}
          text={text}
        />
      )}
    </MotionPresence>
  )
}

Tooltip.locations = Popover.locations
Tooltip.alignments = Popover.alignments

Tooltip.propTypes = {
  alignment: PropTypes.oneOf(Tooltip.alignments),
  anchorElement: CustomPropTypes.Element,
  centerArrow: PropTypes.bool,
  distance: PropTypes.number,
  forwardedRef: CustomPropTypes.ReactRef,
  hideStrategy: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  location: PropTypes.oneOf(Tooltip.locations),
  locationFixed: PropTypes.bool,
  skidding: PropTypes.number,
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  text: PropTypes.string.isRequired,
  visible: PropTypes.bool,
}

Tooltip.displayName = 'Tooltip'
